import { z } from 'zod';

import type {
  AdminCustomer,
  CreateAdminCustomerRequest,
  CustomerCategoryKey,
} from '@mds/shared/types/customer';

import { optionalEmail, optionalPhone, optionalText, picked, requiredText } from '@/lib/validation';

/**
 * The customer editor's working copy, and the conversions either side of it.
 *
 * Adding a customer on the Customers page and adding one while booking a desk
 * or room are the same form, so they share this draft, its checks, and the
 * request body it becomes.
 */

/** The eight fixed categories, in the display order the seed uses. */
export const CUSTOMER_CATEGORIES: { key: CustomerCategoryKey; name: string }[] = [
  { key: 'member', name: 'Member' },
  { key: 'student', name: 'Student' },
  { key: 'graduate', name: 'Graduate' },
  { key: 'freelancer', name: 'Freelancer' },
  { key: 'teacher', name: 'Teacher' },
  { key: 'business', name: 'Business' },
  { key: 'visitor', name: 'Visitor' },
  { key: 'company', name: 'Company' },
];

export const CHANNELS = [
  ['email', 'Email'],
  ['whatsapp', 'WhatsApp'],
  ['sms', 'SMS'],
] as const;

/** The editable half of a record. Everything else is allocated by the API. */
export interface CustomerDraft {
  name: string;
  email: string;
  phone: string;
  categoryKey: CustomerCategoryKey;
  tags: string[];
  interests: string[];
  communicationPreferences: { email: boolean; whatsapp: boolean; sms: boolean };
  internalNotes: string;
}

export const blankCustomer: CustomerDraft = {
  name: '',
  email: '',
  phone: '',
  categoryKey: 'visitor',
  tags: [],
  interests: [],
  communicationPreferences: { email: true, whatsapp: true, sms: false },
  internalNotes: '',
};

export const customerDraftOf = (c: AdminCustomer): CustomerDraft => ({
  name: c.name,
  email: c.email,
  phone: c.phone,
  categoryKey: c.category.key,
  tags: c.tags,
  interests: c.interests,
  communicationPreferences: {
    email: c.communicationPreferences.email,
    whatsapp: c.communicationPreferences.whatsapp,
    sms: c.communicationPreferences.sms,
  },
  internalNotes: c.internalNotes,
});

/** The draft as `POST /admin/customers` — and a booking's `newCustomer` — take it. */
export const createRequestOf = (draft: CustomerDraft): CreateAdminCustomerRequest => ({
  name: draft.name.trim(),
  email: draft.email.trim(),
  phone: draft.phone.trim(),
  categoryKey: draft.categoryKey,
  tags: draft.tags,
  interests: draft.interests,
  communicationPreferences: draft.communicationPreferences,
  internalNotes: draft.internalNotes,
});

/** A list of labels — tags or interests — as the API takes them. */
const labels = (what: string) =>
  z
    .array(
      z
        .string()
        .trim()
        .min(1, 'Remove the empty label.')
        .max(64, 'Keep each label to 64 characters or fewer.'),
    )
    .max(50, `Up to 50 ${what}.`)
    .refine(
      (list) => new Set(list.map((l) => l.toLowerCase())).size === list.length,
      `Each of the ${what} should appear once.`,
    );

/** What the API would reject, checked here so it fails before the round trip. */
export const customerSchema = z.object({
  name: requiredText(160),
  email: optionalEmail,
  phone: optionalPhone,
  categoryKey: picked('Choose a category.'),
  tags: labels('tags'),
  interests: labels('interests'),
  internalNotes: optionalText(10_000),
});

/** Only what actually changed, because PATCH rejects an empty body. */
export function changesBetween(
  before: CustomerDraft,
  after: CustomerDraft,
): Partial<CreateAdminCustomerRequest> {
  const patch: Partial<CreateAdminCustomerRequest> = {};
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

  if (before.name !== after.name) patch.name = after.name.trim();
  if (before.email !== after.email) patch.email = after.email.trim();
  if (before.phone !== after.phone) patch.phone = after.phone.trim();
  if (before.categoryKey !== after.categoryKey) patch.categoryKey = after.categoryKey;
  if (!same(before.tags, after.tags)) patch.tags = after.tags;
  if (!same(before.interests, after.interests)) patch.interests = after.interests;
  if (!same(before.communicationPreferences, after.communicationPreferences)) {
    patch.communicationPreferences = after.communicationPreferences;
  }
  if (before.internalNotes !== after.internalNotes) patch.internalNotes = after.internalNotes;
  return patch;
}
